import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import SiteNav, { BookingButton, WriteMeLink } from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import PageSeo from "@/components/site/PageSeo";
import LogoMarquee from "@/components/site/LogoMarquee";

export const CRM_PATH = "/services/crm-lifecycle";
export const CRM_TITLE = "CRM & Lifecycle Marketing | Déploiement HubSpot / Salesforce — CMO Partner";
export const CRM_DESCRIPTION =
  "Déploiement, refonte ou migration HubSpot et Salesforce, lead scoring, nurturing et alignement marketing-sales. Un CRM qui convertit, piloté par un Fractional CMO. Bordeaux et France.";
const H1 = "CRM & Lifecycle Marketing : déploiement HubSpot / Salesforce et automatisation";
const URL = `https://www.cmopartner.fr${CRM_PATH}`;

export const CRM_FAQ = [
  { q: "Combien coûte un déploiement CRM avec CMO Partner ?", a: "Le coût dépend de l'état de votre CRM actuel, de la plateforme et du format d'accompagnement. Une mission complète (audit, déploiement, automatisations, formation) se réalise en 60 à 90 jours, généralement en format intensif le premier mois. Le tarif est établi sur devis après le diagnostic initial." },
  { q: "Travaillez-vous sur HubSpot et Salesforce ?", a: "Oui, ce sont les deux plateformes principales. CMO Partner intervient aussi sur Brevo, Pipedrive et Monday CRM pour les PME dont le volume ne justifie pas encore HubSpot ou Salesforce." },
  { q: "Peut-on migrer notre CRM existant sans perdre nos données ?", a: "Oui. La migration est précédée d'un audit de la base existante pour identifier les données utiles, corriger les doublons et structurer le modèle de données cible. La migration est testée avant d'être validée en production." },
  { q: "Combien de temps pour voir les premiers effets sur les conversions ?", a: "Les premières séquences actives produisent des résultats mesurables en 30 à 45 jours. La pleine maturité du système (scoring affiné, A/B tests significatifs, cycle de vie complet) se stabilise en 3 à 6 mois." },
  { q: "Formez-vous nos équipes à utiliser le CRM en autonomie ?", a: "Oui, systématiquement. L'objectif est que vos équipes pilotent le CRM sans dépendre en permanence du Fractional CMO. La formation et la documentation font partie de chaque mission." },
  { q: "Intervenez-vous en présentiel ?", a: "Oui, à Bordeaux et en Gironde, notamment pour les ateliers d'alignement marketing-sales et les formations. Le reste de la mission peut se faire à distance, partout en France." },
];

export const CRM_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "CRM & Lifecycle Marketing",
    serviceType: "Déploiement et optimisation CRM",
    url: URL,
    description: CRM_DESCRIPTION,
    provider: {
      "@type": "Organization",
      name: "CMO Partner",
      url: "https://www.cmopartner.fr/",
      founder: { "@type": "Person", name: "Tiphany Rigaudias", jobTitle: ["Fractional CMO", "Directrice marketing externalisée", "Directrice digitale externalisée"], knowsAbout: ["Marketing B2B", "CRM", "Marketing automation", "IA appliquée au marketing", "Formation des équipes marketing"], url: "https://www.cmopartner.fr/a-propos", sameAs: ["https://linkedin.com/in/tiphanyrigaudias"] },
    },
    areaServed: ["Bordeaux", "Gironde", "Nouvelle-Aquitaine", "France"],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: CRM_FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Accueil", item: "https://www.cmopartner.fr/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://www.cmopartner.fr/services" },
      { "@type": "ListItem", position: 3, name: "CRM & Lifecycle Marketing", item: URL },
    ],
  },
];

const H2 = ({ children }: { children: string }) => (
  <h2 className="text-3xl font-bold leading-tight md:text-4xl">
    {children}
    <span className="text-primary">.</span>
  </h2>
);

const problems = [
  { title: "Un CRM mal paramétré ou sous-exploité", text: "L'outil est en place (HubSpot, Salesforce, Brevo…) mais il ne gère pas correctement le pipeline, les données sont sales et les tableaux de bord ne répondent à aucune question business." },
  { title: "Aucune séquence de nurturing", text: "Les leads entrent dans le CRM, puis… rien. Pas de séquence d'activation, pas de suivi automatisé, pas de scoring. Chaque commercial gère ses propres contacts à la main." },
  { title: "Un désalignement marketing / sales", text: "Le marketing génère des leads que la vente ne traite pas, ou la vente estime que les leads ne sont pas qualifiés. Personne n'est d'accord sur ce qu'est un bon lead." },
];

const blocks = [
  {
    label: "Bloc 1",
    title: "Déploiement, refonte ou migration du CRM",
    items: [
      "Audit de l'existant : structure, données, workflows, intégrations",
      "Nettoyage et restructuration de la base de données",
      "Paramétrage des pipelines de vente et des tableaux de bord",
      "Intégration avec vos outils existants (site, ads, support)",
      "Migration de plateforme si nécessaire (par exemple de Brevo vers HubSpot)",
    ],
    note: "Plateformes : HubSpot, Salesforce, Brevo, Pipedrive, Monday CRM.",
  },
  {
    label: "Bloc 2",
    title: "Lifecycle marketing et automatisations",
    items: [
      "Définition des étapes du cycle de vie client (MQL, SQL, client actif, réactivation)",
      "Création des séquences d'activation email et des notifications internes",
      "Mise en place du lead scoring (comportemental et démographique)",
      "Automatisation des campagnes de nurturing et de réengagement",
      "A/B tests des objets, contenus et horaires d'envoi",
    ],
  },
  {
    label: "Bloc 3",
    title: "Formation et transfert de compétences",
    items: [
      "Formation des équipes marketing et commerciales aux nouveaux processus",
      "Documentation des workflows mis en place",
      "Définition du SLA marketing / sales (définition du lead qualifié, délai de traitement)",
      "Tableau de bord partagé pour piloter la performance en autonomie",
    ],
    link: { to: "/services/montee-en-competences-marketing", label: "Aller plus loin : la montée en compétences de votre équipe →" },
  },
];

const results = [
  ["smsmode", "CRM partiel, nurturing absent", "+50 % de leads inbound, +25 % de ROI marketing"],
  ["Lumivero", "Expansion multi-pays, pipeline fragmenté", "+30 % de croissance du CA, 1 M€ de nouveaux marchés"],
];

const comparison = [
  ["Paramétrage technique", "Oui", "Oui"],
  ["Stratégie de segmentation et de messages", "Non", "Oui"],
  ["Définition du lead qualifié avec les sales", "Rarement", "Oui"],
  ["Pilotage des résultats après déploiement", "Non", "Oui"],
  ["Formation et autonomie des équipes", "Sur l'outil", "Sur l'outil et les processus"],
];

const readNext = [
  { label: "Implémenter un CRM à Bordeaux en 2026", to: "/blog/implementer-crm-bordeaux-hubspot-salesforce" },
  { label: "Fractional CMO B2B SaaS", to: "/fractional-cmo-b2b-saas" },
  { label: "Fractional CMO à Bordeaux", to: "/fractional-cmo-bordeaux" },
];

const Table = ({ head, rows }: { head: string[]; rows: string[][] }) => (
  <div className="mt-8 overflow-x-auto rounded-lg border border-border">
    <table className="w-full min-w-[600px] text-left text-sm md:text-base">
      <thead className="bg-secondary">
        <tr>{head.map((h) => <th key={h} scope="col" className="px-5 py-4 font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r[0]} className="border-t border-border">
            {r.map((cell, i) => (
              <td key={i} className={`px-5 py-4 align-top ${i === 0 ? "font-semibold" : "text-muted-foreground"}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CrmLifecyclePage = () => {
  const cta = "Diagnostiquer votre CRM en 30 minutes";
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageSeo path={CRM_PATH} title={CRM_TITLE} description={CRM_DESCRIPTION} />
      <SiteNav />
      <main>
        <section className="border-b border-border px-5 pb-16 pt-20 md:pb-20 md:pt-28">
          <div className="mx-auto max-w-4xl">
            <nav className="mb-8 text-sm text-muted-foreground" aria-label="Fil d'Ariane">
              <Link to="/" className="transition-colors hover:text-primary">Accueil</Link>
              <span className="px-2">/</span>
              <Link to="/services" className="transition-colors hover:text-primary">Services</Link>
              <span className="px-2">/</span>
              <span className="text-foreground">CRM & Lifecycle Marketing</span>
            </nav>
            <h1 className="text-4xl font-bold leading-[1.1] md:text-6xl">
              {H1}
              <span className="text-primary">.</span>
            </h1>
            <p className="mt-8 text-lg leading-relaxed text-muted-foreground md:text-xl">
              Votre CRM contient vos leads, mais il ne convertit pas. Vos emails partent, mais personne ne les a conçus pour vendre. Vos équipes marketing et commerciales travaillent en silo. La mission CRM & Lifecycle de CMO Partner réaligne tout cela en 90 jours.
            </p>
            <div className="mt-10"><BookingButton label={cta} location="crm_hero" large /></div>
          </div>
        </section>

        <LogoMarquee />

        <section className="px-5 py-20 md:py-24">
          <div className="mx-auto max-w-4xl space-y-20">
            <article>
              <H2>Ce que résout une mission CRM & Lifecycle</H2>
              <p className="mt-6 text-lg text-muted-foreground">Trois problèmes reviennent systématiquement dans les PME :</p>
              <div className="mt-8 grid gap-5 md:grid-cols-3">
                {problems.map((p) => (
                  <div key={p.title} className="rounded-lg bg-secondary p-6">
                    <h3 className="text-lg font-bold">{p.title}</h3>
                    <p className="mt-3 leading-relaxed text-muted-foreground">{p.text}</p>
                  </div>
                ))}
              </div>
            </article>

            <article>
              <H2>Les 3 blocs de la mission</H2>
              <div className="mt-8 space-y-6">
                {blocks.map((b) => (
                  <div key={b.title} className="border-t border-border pt-6">
                    <span className="text-sm font-semibold text-primary">{b.label}</span>
                    <h3 className="mt-2 text-xl font-bold">{b.title}</h3>
                    <ul className="mt-4 space-y-3">
                      {b.items.map((it) => (
                        <li key={it} className="flex gap-3 text-lg leading-relaxed text-muted-foreground">
                          <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                          {it}
                        </li>
                      ))}
                    </ul>
                    {"link" in b && b.link && <Link to={b.link.to} className="mt-5 inline-block font-semibold text-primary hover:underline">{b.link.label}</Link>}
                    {b.note && <p className="mt-4 font-semibold">{b.note}</p>}
                  </div>
                ))}
              </div>
              <div className="mt-10 rounded-lg bg-secondary p-7">
                <p className="text-lg font-semibold">30 minutes pour diagnostiquer votre CRM.</p>
                <div className="mt-5"><BookingButton label="Réserver un appel" location="crm_mid" /></div>
              </div>
            </article>

            <article>
              <H2>Un format adapté à chaque étape</H2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                Une mission CRM démarre le plus souvent en format intensif (8 à 12 jours par mois) pour l'audit, le déploiement et les automatisations, puis passe en format léger (2 à 4 jours par mois) pour le pilotage et l'optimisation. Tarif sur devis après le diagnostic.
              </p>
            </article>

            <article>
              <H2>Résultats observés</H2>
              <Table head={["Client", "Situation initiale", "Résultat obtenu"]} rows={results} />
            </article>

            <article>
              <H2>Pourquoi un Fractional CMO plutôt qu'un intégrateur CRM ?</H2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                Un intégrateur CRM configure l'outil. Il s'arrête au déploiement technique. Un <Link to="/fractional-cmo" className="text-foreground underline hover:text-primary">Fractional CMO</Link> configure l'outil ET définit la stratégie qui va dedans : quelles séquences, pour quels segments, avec quels messages, mesurées sur quels KPIs. Il aligne aussi les équipes marketing et commerciales sur les processus. Le résultat : un CRM qui tourne, pas seulement un CRM installé.
              </p>
              <Table head={["Critère", "Intégrateur CRM", "Fractional CMO"]} rows={comparison} />
            </article>
          </div>
        </section>

        <section className="border-t border-border bg-secondary px-5 py-20">
          <div className="mx-auto max-w-4xl">
            <H2>FAQ : CRM & Lifecycle Marketing</H2>
            <div className="mt-10">
              {CRM_FAQ.map((f) => (
                <details key={f.q} className="group border-t border-border py-6">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-lg font-bold">
                    {f.q}
                    <span className="text-primary transition-transform group-open:rotate-45" aria-hidden="true">+</span>
                  </summary>
                  <p className="mt-3 leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-border px-5 py-16">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-xl font-bold">À lire aussi</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {readNext.map((r) => (
                <Link key={r.to} to={r.to} className="rounded-lg border border-border p-5 transition-colors hover:border-primary">
                  <span className="font-semibold">{r.label}</span>
                  <ArrowRight className="mt-4 h-4 w-4 text-primary" aria-hidden="true" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section id="booking" className="scroll-mt-16 bg-foreground px-5 py-24 text-center text-background md:py-28">
          <div className="mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold md:text-5xl">Un CRM qui tourne, pas seulement installé<span className="text-primary">.</span></h2>
            <p className="mt-6 text-lg text-background/60">30 minutes pour diagnostiquer votre CRM et identifier vos leviers prioritaires.</p>
            <div className="mt-10"><BookingButton label={cta} location="crm_final" large /><WriteMeLink /></div>
            <p className="mt-6 text-sm text-background/50">Basée à Bordeaux · Disponible partout en France</p>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default CrmLifecyclePage;
